'use client'

import { PointerEvent, RefObject, useRef } from 'react'

import { cn } from '@/lib/classnames'

import { COPIES, HowItWorksStep, TRANSITION_MS } from './steps'

type HowItWorksTabsProps = {
  steps: HowItWorksStep[]
  active: number
  count: number
  middleStart: number
  offset: number
  ready: boolean
  motion: boolean
  label: string
  viewportRef: RefObject<HTMLDivElement>
  trackRef: RefObject<HTMLDivElement>
  buttonRefs: RefObject<(HTMLButtonElement | null)[]>
  onSelect: (index: number) => void
  onStep: (delta: number) => void
  onPause: (paused: boolean) => void
}

const HowItWorksTabs = ({ steps, active, count, middleStart, offset, ready, motion, label, viewportRef, trackRef, buttonRefs, onSelect, onStep, onPause }: HowItWorksTabsProps) => {
  const suppressClick = useRef(false)
  const pointer = useRef<{ id: number; x: number } | null>(null)

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    pointer.current = { id: event.pointerId, x: event.clientX }
  }

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const current = pointer.current
    pointer.current = null
    if (!current || current.id !== event.pointerId) return
    const delta = event.clientX - current.x
    if (Math.abs(delta) < 48) return
    suppressClick.current = true
    window.setTimeout(() => {
      suppressClick.current = false
    }, 0)
    onStep(delta < 0 ? 1 : -1)
  }

  return (
    <div
      className='relative mt-10'
      onMouseEnter={() => onPause(true)}
      onMouseLeave={() => onPause(false)}
      onFocus={() => onPause(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onPause(false)
      }}
    >
      <div
        ref={viewportRef}
        className={cn('touch-pan-y overflow-hidden', ready ? 'visible' : 'invisible')}
        role='tablist'
        aria-label={label}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          pointer.current = null
        }}
        onKeyDown={event => {
          if (event.key === 'ArrowRight') {
            event.preventDefault()
            onStep(1)
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault()
            onStep(-1)
          }
        }}
      >
        <div
          ref={trackRef}
          className='flex w-max items-center gap-3 py-1'
          style={{
            transform: `translate3d(${offset}px, 0, 0)`,
            transition: motion ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : 'none',
          }}
        >
          {Array.from({ length: count * COPIES }, (_, index) => {
            const step = steps[index % count]
            const selected = index === active
            const isClone = index < middleStart || index >= middleStart + count
            return (
              <button
                key={index}
                ref={node => {
                  if (buttonRefs.current) buttonRefs.current[index] = node
                }}
                id={`how-it-works-tab-${index}`}
                type='button'
                role='tab'
                aria-selected={selected}
                aria-controls='how-it-works-panel'
                aria-hidden={isClone && !selected ? true : undefined}
                tabIndex={isClone ? -1 : 0}
                onClick={() => {
                  if (suppressClick.current) {
                    suppressClick.current = false
                    return
                  }
                  onSelect(index)
                }}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F3603C] md:px-6 md:text-base',
                  selected ? 'bg-[#F3603C] text-white' : 'bg-white text-slate-500 hover:bg-slate-50',
                )}
              >
                {(index % count) + 1}. {step.title}
              </button>
            )
          })}
        </div>
      </div>
      <div className='pointer-events-none absolute inset-y-0 left-0 z-10 w-1/4 bg-gradient-to-r from-white to-transparent' />
      <div className='pointer-events-none absolute inset-y-0 right-0 z-10 w-1/4 bg-gradient-to-l from-white to-transparent' />
    </div>
  )
}

export default HowItWorksTabs
